import { useNotificationCenter } from '~/composables/useNotificationCenter'
import { useLocale } from '~/composables/useLocale'
import { useAuthStore } from '~/stores/useAuthStore'

const SHINY_BANNER_KEY = 'poke-idle-shiny-banner-seen'

/**
 * Compteur de suppression : pendant un batch hatch pension, les toasts
 * « Shiny éclos » sont déjà émis — éviter le double comptage journal (x2).
 */
let suppressShinyObtainedCelebration = 0

export function withSuppressedShinyObtainedCelebration<T>(fn: () => T): T {
  suppressShinyObtainedCelebration += 1
  try {
    return fn()
  } finally {
    suppressShinyObtainedCelebration -= 1
  }
}

export function useCelebrationBanners() {
  const player = usePlayerStore()
  const inventory = useInventoryStore()
  const auth = useAuthStore()
  const { notify } = useNotificationCenter()
  const { t } = useLocale()

  /** Évite les toasts lors de l'hydratation serveur (load / reset), pas pendant le jeu. */
  const celebrationsReady = ref(false)
  const lastLevel = ref(0)
  const lastBadges = ref(0)
  const lastShinyCount = ref(0)

  function syncBaselines() {
    lastLevel.value = player.level
    lastBadges.value = player.badges
    lastShinyCount.value = inventory.collection.filter((p) => p.isShiny).length
  }

  watch(
    () => auth.gameLoaded && auth.isAuthenticated,
    (ready) => {
      if (ready) {
        syncBaselines()
        celebrationsReady.value = true
      } else {
        celebrationsReady.value = false
        syncBaselines()
      }
    },
    { immediate: true },
  )

  watch(
    () => player.level,
    (lvl, prev) => {
      if (!celebrationsReady.value) {
        lastLevel.value = lvl
        return
      }
      const before = prev ?? lastLevel.value
      if (lvl > before) {
        notify('progression', t(`Niveau ${lvl} !`, `Level ${lvl}!`), { icon: '⬆️' })
        if (lvl >= 25 && before < 25) {
          notify('progression', t('Mini-jeux débloqués !', 'Minigames unlocked!'), {
            icon: '🎲',
            coalesceKey: 'unlock',
          })
        }
      }
      lastLevel.value = lvl
    },
  )

  watch(
    () => player.badges,
    (badges, prev) => {
      if (!celebrationsReady.value) {
        lastBadges.value = badges
        return
      }
      const before = prev ?? lastBadges.value
      if (badges > before) {
        notify('progression', t(`Badge obtenu ! (${badges})`, `Badge earned! (${badges})`), {
          icon: '🏅',
          coalesceKey: 'badge',
        })
      }
      lastBadges.value = badges
    },
  )

  watch(
    () => inventory.collection.filter((p) => p.isShiny).length,
    (count) => {
      if (!celebrationsReady.value || suppressShinyObtainedCelebration > 0) {
        lastShinyCount.value = count
        return
      }
      const delta = count - lastShinyCount.value
      if (delta > 0) {
        notify('shiny', t('✨ Shiny obtenu ! Consulte ton inventaire.', '✨ Shiny obtained! Check your inventory.'), {
          icon: '✨',
          type: 'success',
          duration: 8000,
          coalesceKey: 'shiny-obtained',
          countDelta: delta,
        })
        try {
          localStorage.setItem(SHINY_BANNER_KEY, String(Date.now()))
        } catch {
          /* ignore */
        }
      }
      lastShinyCount.value = count
    },
  )
}
